/**
 * HigbieKLPage — Higbie 1935 penetration theory mass-transfer coefficient
 *
 * k_L = 2·√(D_O2 / (π·t_contact))  where t_contact = d_b / v_rise
 * Cross-check on the empirical Stenstrom & Rosso 2008 SOTE correlation.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Layers, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { specFromLegacy, type LegacyModSpec, bubbleDiameterMm, bubbleRiseVelMs, sotePerMetre } from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12 };

// Stokes-Einstein O2 diffusivity in water at temperature T (°C)
function D_O2_water(T: number): number {
  return 1.97e-9 * Math.exp(-1896 / (T + 273.15)) * 1e3;   // m²/s, calibrated to ~2.1e-9 at 20°C
}

export default function HigbieKLPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('higbie_kL');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));

  useEffect(() => {
    const unsub = onPersistChange(k => { if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }); });
    return () => unsub();
  }, []);

  const R = useMemo(() => {
    const dSpec = specFromLegacy(spec);
    const T = spec.waterTempC;
    const d_b_mm = bubbleDiameterMm(dSpec, T);
    const d_b_m = d_b_mm / 1000;
    const v_rise = bubbleRiseVelMs(d_b_mm, T);
    const t_contact = d_b_m / Math.max(0.01, v_rise);       // surface renewal time
    const D = D_O2_water(T);
    const k_L = 2 * Math.sqrt(Math.max(1e-12, D / (Math.PI * t_contact)));
    const Sh = k_L * d_b_m / Math.max(1e-12, D);             // Sherwood
    const Sh_frossling = 2;                                    // lower bound
    const sote_stenstrom = sotePerMetre(d_b_mm);
    // Approximate SOTE-per-metre from Higbie k_L × specific area
    // Specific bubble area a = 6 × hold-up / d_b. For typical eps_gas ~ 0.05:
    const eps_gas = 0.03;
    const a_specific = 6 * eps_gas / Math.max(1e-6, d_b_m);
    const sote_higbie = Math.min(15, k_L * a_specific * 100 / 9.09);     // very approximate
    return { d_b_mm, v_rise, t_contact, D, k_L, Sh, Sh_frossling, sote_stenstrom, sote_higbie };
  }, [spec]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Layers} title="Module — Higbie Penetration kₗ" subtitle="Surface-renewal mass-transfer cross-check"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('higbie_kL')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="higbie_kL" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">k_L (Higbie)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{R.k_L.toExponential(2)}</div>
          <div className="text-xs text-slate-500 mt-1">m/s</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Contact time</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.t_contact, 4)} s</div>
          <div className="text-xs text-slate-500 mt-1">d_b / v_rise</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Sherwood</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.Sh, 1)}</div>
          <div className="text-xs text-slate-500 mt-1">Frössling LB = 2</div>
        </div>
        <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-violet-300">SOTE Higbie vs Stenstrom</div>
          <div className="text-sm font-bold text-violet-200 mt-1">{fmt(R.sote_higbie, 1)} vs {fmt(R.sote_stenstrom, 1)} %/m</div>
          <div className="text-xs text-slate-500 mt-1">Δ {fmt(R.sote_higbie - R.sote_stenstrom, 1)} %/m</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Methodology</div>
        <p>Higbie 1935 surface-renewal theory: k_L = 2·√(D/(π·t_c)) where t_c is the contact time of a surface
        element with the gas-liquid interface. For rising bubbles, t_c ≈ d_b / v_rise. Sherwood number Sh = k_L·d_b/D
        should exceed the Frössling lower bound of 2 for ellipsoidal bubbles.</p>
        <p className="mt-2">This page is a <strong className="text-violet-300">cross-check</strong> on the
        empirical Stenstrom & Rosso 2008 SOTE correlation. When the two agree within 20% the design is
        well-supported; when they disagree by &gt;30% the bubble regime may be outside the calibration
        envelope of the Stenstrom correlation (very small or very large bubbles).</p>
        <p className="italic text-slate-500 mt-2">References: Higbie 1935; Danckwerts 1951; Stenstrom & Rosso 2008.</p>
      </div>
    </div>
  );
}
